import React from 'react';
import {
  Box,
  IconButton,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { selectors } from '../../../../../store/assignments';
import { AulaState } from '@core/types/state';
import useIsClamped from '@app/hooks/useIsClamped';
import { ResponsiveAssignment } from '@core/assignments/types';
import { createStyles } from './AssignmentCard.styles';

type AssignmentExtensionsModalProps = {
  open: boolean;
  assignment: ResponsiveAssignment;
  onClose: () => void;
};

type CohortRow = {
  id: string;
  title: string;
  submissionsCount: number;
  studentsCount: number;
  isMain: boolean;
};

type OverflowTextProps = {
  text: string;
  isDesktop: boolean;
  id?: string;
  wrapperSx?: Record<string, unknown>;
  textSx: Record<string, unknown>;
  clampLines?: number;
};

const getExtensionLabel = (extension: any, index: number) => {
  if (typeof extension === 'string') {
    return extension;
  }

  return (
    extension?.title ||
    extension?.name ||
    extension?.displayName ||
    `Extension ${index + 1}`
  );
};

const getStudentsCount = (item: any) =>
  item?.studentCount ?? item?.students?.length ?? 0;

const OverflowText = ({
  text,
  isDesktop,
  id,
  wrapperSx,
  textSx,
  clampLines = 2,
}: OverflowTextProps) => {
  const [textRef, isTextClamped] = useIsClamped<HTMLDivElement>(text);

  return (
    <Tooltip
      disableInteractive
      title={text}
      disableFocusListener={!isDesktop || !isTextClamped}
      disableHoverListener={!isDesktop || !isTextClamped}
      disableTouchListener={!isDesktop || !isTextClamped}
    >
      <Box sx={{ ...(wrapperSx || {}) }}>
        <Box
          id={id}
          ref={textRef}
          sx={{ ...textSx, WebkitLineClamp: clampLines }}
        >
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
};

const CohortRowItem = ({
  row,
  styles,
  isFirst,
}: {
  row: CohortRow;
  styles: ReturnType<typeof createStyles>;
  isFirst: boolean;
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        ...styles.extensionRow,
        ...(isFirst ? styles.extensionRowFirst : {}),
      }}
    >
      {!row.isMain && <Icons.ExtensionOutlined sx={styles.extensionRowIcon} />}
      <OverflowText
        text={row.title}
        isDesktop={isDesktop}
        wrapperSx={styles.extensionRowTitleWrapper}
        textSx={styles.extensionRowText}
      />
      <Typography sx={styles.extensionRowCount}>
        ({row.submissionsCount}/{row.studentsCount})
      </Typography>
    </Box>
  );
};

const AssignmentExtensionsModal = ({
  open,
  assignment,
  onClose,
}: AssignmentExtensionsModalProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const parentAssignmentId =
    assignment.id || assignment.objectId || assignment.externalId;
  const parentAssignmentSubmissions = useSelector((state: AulaState) =>
    selectors.getAssignmentSubmissions(state, parentAssignmentId)
  );
  const rows: CohortRow[] = [
    {
      id: parentAssignmentId,
      title: 'Main Cohort',
      submissionsCount: assignment?.submissions?.length ?? 0,
      studentsCount: parentAssignmentSubmissions.length,
      isMain: true,
    },
    ...(assignment.extensions || []).map((extension: any, index: number) => {
      const extensionId = extension?.id || extension?.objectId || `${index}`;

      return {
        id: extensionId,
        title: getExtensionLabel(extension, index),
        submissionsCount: extension?.submissions?.length ?? 0,
        studentsCount: getStudentsCount(extension),
        isMain: false,
      };
    }),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="assignment-extensions-modal-title"
      aria-modal="true"
    >
      <Box sx={styles.extensionsModal} role="dialog">
        <Box sx={styles.extensionsModalHeader}>
          <Typography
            id="assignment-extensions-modal-title"
            sx={styles.extensionsModalTitle}
          >
            <OverflowText
              text={assignment.title}
              isDesktop={isDesktop}
              wrapperSx={styles.extensionsModalTitleWrapper}
              textSx={styles.extensionsModalTitle}
              clampLines={2}
            />
          </Typography>
          <IconButton
            aria-label="Close extensions modal"
            onClick={onClose}
            sx={styles.extensionsModalClose}
          >
            <Tooltip
              title="Close"
              disableHoverListener={!isDesktop}
              disableFocusListener={!isDesktop}
              disableTouchListener
            >
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>

        <Box sx={styles.extensionsModalContent}>
          <Box sx={styles.extensionsList}>
            {rows.map((row, index) => (
              <CohortRowItem
                key={row.id}
                row={row}
                styles={styles}
                isFirst={index === 0}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssignmentExtensionsModal;
